import type {AiChatPromptBoxOptions, AiChatSource, ChatOnLoadParams, ChatType, RegisterAiChatControls, SurfaceBias} from '@Components/ai-chat-window/ai-chat-window.types';
import type {MakeTextEditableLimit} from '@Components/ai-chat-window/lib/make-text-editable.library';
import type {AiChatBannerData} from '@AiChat/components/ai-chat-banner/ai-chat-banner.types';
import type {RecommendedTemplate} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/components/templates-for-inspiration-tile/templates-for-inspiration-tile.types';

export const AI_CHAT_HOME_SCROLL_ID = 'ai-chat-home-scroll-container';
export const AI_CHAT_PROMPT_BOX_ID = 'ai-chat';
export const AI_CHAT_QUICK_TIPS_URL_SEGMENT = 'quickTips';
export const AI_CHAT_ROOT_URL_SEGMENT = 'chat';
export const AI_CHAT_ASSETS_URL_SEGMENT = 'assets';
export const AI_CHAT_AGENT_URL_SEGMENT = 'agent';
export const AI_CHAT_MEMORY_URL_SEGMENT = 'memory';

export interface HistoryItem {
  idAiChat: string;
  title?: string;
  assetHashedFilename?: string;
  type: ChatType;
  updatedAt: number;
  source?: AiChatSource | null;
}

export enum AiChatGeneratedAssetType {
  IMAGE = 'image',
  VIDEO = 'video',
}

export enum AiChatGeneratedAssetFilter {
  ALL = 'all',
  IMAGES = 'images',
  VIDEOS = 'videos',
}

export interface AiChatGeneratedAsset {
  type: AiChatGeneratedAssetType;
  assetHashedFilename: string;
  idAiChat: string;
  width: number | null;
  height: number | null;
  duration: number | null;
  hasAudio: boolean | null;
  startTime: number | null;
  endTime: number | null;
  createdOn: string;
  messageId: string | null;
}

/** One consolidated fact from the memory library. The endpoint also returns ranking fields, which only a recall populates and this page has no use for. */
export interface UserMemoryFact {
  id: string;
  fact: string;
  updateTime: string | null;
}

/** One named subject the user taught us pixels for — the second memory tier, shown beside the facts. */
export interface UserMemoryVisualEntity {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  updateTime: string;
}

export interface SavedAccountBrandKitColor {
  hex: string;
  role: string;
}

export interface SavedAccountBrandKit {
  name: string;
  colors: SavedAccountBrandKitColor[];
}

export interface SavedAccountBusinessProfile {
  companyName: string;
  phone: string;
  email: string;
  website: string;
}

/** Absolute editor URLs the server builds (site_url) for each part of the saved account; the page opens them in a new tab. */
export interface SavedAccountEditUrls {
  accountName: string;
  brandKits: string;
  businessProfile: string;
}

/** What is saved on the account itself, shown above the memories. The endpoint's kit ids are deliberately not typed: the page never renders them. */
export interface SavedAccountSummary {
  accountName: string | null;
  brandKits: SavedAccountBrandKit[];
  businessProfile: SavedAccountBusinessProfile | null;
  canCreateBrandKit: boolean;
  editUrls: SavedAccountEditUrls;
}

export interface UserMemories {
  facts: UserMemoryFact[];
  visualEntities: UserMemoryVisualEntity[];
  /** Null when the saved data could not be read; the memories still show. */
  account: SavedAccountSummary | null;
}

export const EMPTY_USER_MEMORIES: UserMemories = {
  facts: [],
  visualEntities: [],
  account: null,
};

/** FAILED is distinct from an empty LOADED: "nothing stored yet" after an unreadable corpus is a claim we cannot make. */
export enum MemoryLoadState {
  LOADING,
  LOADED,
  FAILED,
}

export interface RecommendedTemplatesData {
  templates: RecommendedTemplate[];
  templateSearchTerm: string | null;
}

export interface AiChatConversationInput {
  chatOnLoadParams: ChatOnLoadParams;
  surfaceBias?: SurfaceBias;
  promptBoxOptions?: AiChatPromptBoxOptions;
  registerControls?: RegisterAiChatControls;
}

export interface AiChatCapability {
  nameKey: string;
  descriptionKey: string;
}

export interface AiChatCapabilityGroup {
  headingKey: string;
  icon: string;
  capabilities: AiChatCapability[];
}

export interface AiChatChristmasGiftOffer {
  promoCode: string;
  discountFraction: number;
}

export interface AiChatProps {
  chatOnLoadParams: ChatOnLoadParams;
  credits: number;
  isPaygUserWithAiChatAccess: boolean;
  isReturningUser: boolean;
  isSuperUser: boolean;
  hasGeneratedAssets: boolean;
  isMemoryEnabled: boolean;
  shouldShowAssetsIntroSubtitle?: boolean;
  isAiChatCreateAVideoFeatureEnabled: boolean;
  isTemplateRedesignFeatureEnabled: boolean;
  previewSeason: string | null;
  christmasGiftOffer?: AiChatChristmasGiftOffer | null;
  promptBoxPlaceholderKey: string | null;
  landingSource?: AiChatSource;
  canUseEditableTextLayers: boolean;
  isMakeDesignEditableEnabled: boolean;
  makeDesignEditableCreditCost: number;
  chargeForMakeTextEditable: boolean;
  makeTextEditableLimit: MakeTextEditableLimit | null;
  banner?: AiChatBannerData;
  capabilityGroups: AiChatCapabilityGroup[];
}
